import type { RouterConfig } from "@nuxt/schema";
import type { RouteRecordRaw } from "vue-router";

import { Language, Pages } from "~/utils/constants";

const en = Pages[Language.EN];
const fr = Pages[Language.FR];

export const checkoutRoutes: RouteRecordRaw[] = [
  {
    name: en.search,
    path: `:searchKeyword(${en.search}|${fr.search})`,
    component: () => import("~/pages/search/index.vue"),
  },
  {
    /**
     * The inventorySlug is required, but we make it optional to redirect to
     * the translated fallback page in our localization middleware if it's missing.
     */
    name: en.details,
    path: `:detailsKeyword(${en.details}|${fr.details})/:inventorySlug?`,
    component: () => import("~/pages/details/index.vue"),
    meta: {
      layout: "details",
    },
  },
];

/**
 * This file is used to configure the routes for the application.
 * It needs to be inside the /app folder to be picked up by the Nuxt.js.
 *
 * We decided to drop the use of the Nuxt pages directory in favor of a more
 * flexible approach using the router options. This allows us to define the
 * routes programmatically and apply advanced matching patterns for dynamic
 * routes.
 *
 * In this approach we can easily maintain the URL structure consistent with
 * Google SEO guidelines and provide a better user experience by using
 * translated slugs in the URL.
 *
 * Examples of translated slugs in the URL:
 * - /en/checkout/search
 * - /fr/achat/recherche
 *
 * @link https://router.vuejs.org/api/interfaces/routeroptions.html#routes
 */
const routerConfig: RouterConfig = {
  routes: () => [
    {
      name: "home",
      path: `/:language(${Language.EN}|${Language.FR})`,
      children: [
        {
          name: en.checkout,
          path: `:checkoutKeyword(${en.checkout}|${fr.checkout})`,
          children: checkoutRoutes,
        },
      ],
    },
    {
      name: "admin",
      path: "/admin",
      children: [
        {
          path: "",
          name: "admin-home",
          component: () => import("~/pages/admin/index.vue"),
        },
        {
          path: "config",
          name: "admin-config",
          component: () => import("~/pages/admin/config.vue"),
        },
        {
          path: ":crud",
          name: "admin-crud",
          component: () => import("~/pages/admin/[crud]/index.vue"),
        },
        {
          path: ":crud/:id",
          name: "admin-crud-id",
          component: () => import("~/pages/admin/[crud]/[id].vue"),
        },
      ],
      meta: {
        layout: "admin",
      },
    },
    {
      name: "auth",
      path: "/auth",
      children: [
        {
          path: "login",
          name: "auth-login",
          component: () => import("~/pages/auth/login.vue"),
        },
        {
          path: "profile",
          name: "auth-profile",
          component: () => import("~/pages/auth/profile.vue"),
        },
      ],
      meta: {
        layout: "empty",
      },
    },
  ],
};

export default routerConfig;
